import type { DISPLAY_STYLE_OPTIONS } from "../components/Paywalls/Admin/PaywallsAdmin/FormModal/CheckoutTab/constants";
import type { FileMetadata } from "../components/Paywalls/Admin/PaywallsAdmin/FormModal/CheckoutTab/Sidebar/Properties/Fields/ImageAndVideoUploader/types";

export const ACTION_TYPE = {
  DO_NOTHING: "do_nothing",
  REDIRECT_TO: "redirect_to",
};

export const COUNTDOWN_TYPE = {
  REMAINING_TIME: "remaining_time",
  FINISH_AT: "finish_at",
};

export const EMOJI_TYPE = {
  EMOJI: "emoji",
  CUSTOM: "custom",
  SHAPES: "shapes",
  ICONS: "icons",
};

export interface Icon {
  emoji_type: (typeof EMOJI_TYPE)["ICONS"];
  identifier: string;
}

export interface ShapeIcon {
  emoji_type: (typeof EMOJI_TYPE)["SHAPES"];
  identifier: string;
}

export interface CustomIcon {
  emoji_type: (typeof EMOJI_TYPE)["CUSTOM"];
  filename: string;
  signed_id: string;
}

export interface EmojiIcon {
  emoji: string;
  emoji_type: (typeof EMOJI_TYPE)["EMOJI"];
}

export type BadgePropertyIcon = EmojiIcon | CustomIcon | ShapeIcon;
export type BenefitPropertyIcon = EmojiIcon | CustomIcon | ShapeIcon | Icon;

export interface BenefitProperty {
  description: string;
  icon: BenefitPropertyIcon;
  id?: string;
  title: string;
}

export interface BadgeProperty {
  background_color: string;
  icon: BadgePropertyIcon;
  id?: string;
  label: string;
  text_color: string;
  visible: boolean;
}

export interface TestimonialProperty {
  avatar_image?: string;
  avatar_image_url?: string;
  display_style: {
    color: string;
    type: (typeof DISPLAY_STYLE_OPTIONS)[keyof typeof DISPLAY_STYLE_OPTIONS];
  };
  headline: string;
  id?: string;
  name: string;
  quote: string;
  visible: boolean;
}

export interface CountdownProperties {
  action: {
    action_type: (typeof ACTION_TYPE)[keyof typeof ACTION_TYPE];
    redirect_to?: string;
  };
  countdown_type: (typeof COUNTDOWN_TYPE)[keyof typeof COUNTDOWN_TYPE];
  displayed_text: string;
  enabled: boolean;
  finish_at?: string;
  remaining_time?: number;
  remaining_time_interval?: "minutes" | "seconds";
}

export interface PriceCallout {
  callout_text: string;
  highlighted: boolean;
  price_id: number | string;
}

export interface PriceCalloutProperties {
  callout_color: string;
  prices: Array<PriceCallout>;
}

export interface ReducedPrice {
  amount: number | string;
  enabled: boolean;
  original_amount?: string; // Only available in the Paywall forms
  original_upfront_amount?: string; // Only available in the Paywall forms
  price_id: number | string;
  upfront_amount: number | string;
}

export interface ReducedPriceProperties {
  prices: Array<ReducedPrice>;
}

export interface DetailedProperties {
  background_color: string;
  cover?: FileMetadata;
  cover_video_properties?: {
    custom_cover_image?: FileMetadata;
    mute_video?: boolean;
    loop_video?: boolean;
    autoplay?: boolean;
  };
  description: string;
  title: string;
}

export interface MediaBlockProperty {
  background_color: string;
  cover?: FileMetadata;
  description: string;
  id?: string; // Only available on the form
  title: string;
}

export interface MediaBlockProperties {
  enabled: boolean;
  media_blocks: Array<MediaBlockProperty>;
}

export interface PaywallCustomization {
  badge_properties: {
    enabled: boolean;
    badges: BadgeProperty[];
  };
  benefit_properties: {
    enabled: boolean;
    title: string;
    benefits: BenefitProperty[];
  };
  community_id?: number;
  countdown_properties: CountdownProperties;
  cover_image: string;
  cover_image_url: string;
  created_at?: string;
  customizable_id?: number;
  customizable_type?: "Paywall";
  detailed_properties?: DetailedProperties;
  id?: number;
  media_block_properties: MediaBlockProperties;
  price_callout_properties: PriceCalloutProperties;
  reduced_price_properties: ReducedPriceProperties;
  testimonial_properties: {
    enabled: boolean;
    testimonials: TestimonialProperty[];
  };
  updated_at?: string;
}
